import {useMemo} from 'react'
import type {SubIssueSummaryData} from '@github-ui/issue-create/Model'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'

// Broad node derived from the allowlisted `IssueViewerSecondaryViewQuery` generated type, which contains every field
// `useSubIssuesSummary` reads. Deriving from codegen keeps these shapes in sync automatically when the query changes.
type IssueNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>
type SubIssueNode = NonNullable<NonNullable<IssueNode['subIssues']['nodes']>[number]>

/**
 * The fields `useSubIssuesSummary` reads off an `Issue`. Consumers select these inline (the exact set depends on the
 * `fetchSubIssues` argument they pass) and forward the result here. All fields are optional because each consumer only
 * selects the branch it needs.
 */
export type SubIssuesSummaryFragment = Partial<Pick<IssueNode, 'subIssuesSummary' | 'subIssuesConnection'>> & {
  readonly subIssues?: {readonly nodes: ReadonlyArray<Pick<SubIssueNode, 'closed'> | null> | null} | null
}

/**
 * `subIssuesSummary` is updated asynchronously, meaning that it is not guarunteed to be synchronized with `subIssues`.
 * This can cause issues when the sub-issues list is visible, as `subIssuesSummary` may not align with the list of
 * visible issues. To resolve this, we calculate the summary on the client side if the sub-issues list is loaded.
 *
 * Consumers should *always* prefer calling `useSubIssuesSummary` over reading the `subIssuesSummary` field directly.
 */
export function useSubIssuesSummary(data?: SubIssuesSummaryFragment): SubIssueSummaryData {
  const {subIssuesSummary, subIssues, subIssuesConnection} = data ?? {}
  const total = subIssues?.nodes?.length ?? subIssuesConnection?.totalCount ?? subIssuesSummary?.total ?? 0
  const completed = subIssues?.nodes?.filter(issue => issue?.closed).length ?? subIssuesSummary?.completed ?? 0

  return useMemo(
    () => ({
      total,
      completed,
      percentCompleted: total !== 0 ? Math.floor((completed / total) * 100) : 0,
    }),
    [total, completed],
  )
}
